import { useCallback, useEffect } from 'react'

import { Button, FixedBanner } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { routes } from '@services/sections/modal/routes'
import { useMessageTransmitter } from '@shared/wallet/hooks/useMessageTransmitter'
import useTranslation from 'next-translate/useTranslation'

import type { BlockchainLocationMetadata, Maybe } from '@shared/graphql'
import type { SupportedChainId } from '@shared/wallet/constants/chain'

export interface OnChainReceiveSectionProps {
  /**
   * Chained transfer metadata.
   */
  transferMetadata?: Maybe<BlockchainLocationMetadata>
  /**
   * Is wallet connected.
   */
  isConnected: boolean
  /**
   * Blockchain ID.
   */
  chainId: SupportedChainId
  /**
   * Has wallet error.
   */
  hasWalletError?: boolean
  /**
   * Custom className.
   */
  className?: string
}

export const OnChainReceiveSection: React.FC<OnChainReceiveSectionProps> = ({
  transferMetadata,
  isConnected,
  chainId,
  hasWalletError = false,
  className,
}) => {
  const { t } = useTranslation('ace')
  const { router } = useModal()

  const { receiveMessage, isPending, isSuccess, error } =
    useMessageTransmitter(chainId)

  const handleReceive = useCallback(() => {
    const { message, attestation } = transferMetadata ?? {}

    if (message && attestation) {
      receiveMessage(message, attestation)
    }
  }, [receiveMessage, transferMetadata])

  useEffect(() => {
    if (isSuccess) {
      router.push(routes.blockchain.aceSendOnchain.success)
    }
  }, [isSuccess, router])

  return (
    <div className={className}>
      {isConnected && (
        <Button
          className="mt-6 w-full"
          disabled={hasWalletError}
          loading={isPending}
          onClick={handleReceive}
          variant="primary"
        >
          {t(`sendOnchain.receive.cta`)}
        </Button>
      )}

      <FixedBanner className="mt-6" status="error" visible={error != null}>
        <FixedBanner.Title>
          {t(`sendOnchain.receive.error.title`)}
        </FixedBanner.Title>
        <FixedBanner.Description>
          {t(`sendOnchain.receive.error.description`)}
        </FixedBanner.Description>
      </FixedBanner>
    </div>
  )
}
